import { apiFetch } from './api';

const apiUrl = import.meta.env.VITE_API_URL;

export interface TipoTarefa {
    tipoTarefaId: number;
    nome: string;
    dataCriacao: string;
}

/** O que o vendedor escolhe ao registrar uma tarefa padrão, e o que a escolha faz. */
export interface DesfechoTarefa {
    desfechoId: number;
    rotulo: string;
    ordem: number;
    /** move | lose | reschedule | close */
    acao: 'move' | 'lose' | 'reschedule' | 'close';
    statusDestinoId: number | null;
    diasReagendamento: number | null;
}

export interface Tarefa {
    tarefaId: number;
    usuarioCriacaoId: number;
    conversaId: number | null;
    descricao: string;
    estaConcluida: boolean;
    tipoTarefaId: number;
    dataRetorno: string | null;
    dataCriacao: string;
    // Tarefas padrão por etapa
    responsavelId: number | null;
    /** manual | default */
    origem: 'manual' | 'default';
    modeloId: number | null;
    desfechoId: number | null;
    desfechoRotulo: string | null;
    /** open | done | superseded */
    estado: 'open' | 'done' | 'superseded';
    tentativas: number;
    /** Desfechos disponíveis (só na tarefa padrão aberta). */
    desfechos: DesfechoTarefa[];
    /** Contato e etapa da oportunidade, quando a tarefa tem uma. Vêm junto da tarefa
     *  para a lista não depender da lista de oportunidades, que recorta campanha sem resposta. */
    nomeContato?: string | null;
    statusId?: number | null;
    /** Temperatura do lead — decide se mover a etapa pergunta a temperatura antes. */
    tipoLeadId?: number | null;
    /** De quando é a oportunidade, de onde ela veio (PlataformaEnum) e quanto vale. */
    criadaEm?: string | null;
    origemId?: number | null;
    valor?: number | null;
}

export interface CreateTarefaDTO {
    descricao: string;
    conversaId: number | null;
    tipoTarefaId: number;
    dataRetorno: string | null;
    responsavelId?: number | null;
}

export interface RegistrarDesfechoDTO {
    desfechoId: number;
    motivoPerdaId?: number | null;
    motivoPerdaDetalhe?: string | null;
    tipoLeadId?: number | null;
}

export interface DesfechoRegistrado {
    tarefa: Tarefa;
    proximaTarefa: Tarefa | null;
    novoStatusId: number | null;
}

export interface UpdateTarefaDTO {
    tarefaId: number;
    responsavelId?: number | null;
    descricao?: string;
    estaConcluida?: boolean;
    tipoTarefaId?: number;
    dataRetorno?: string | null;
}

interface ApiResponse<T> {
    sucesso: boolean;
    mensagem: string;
    dados: T;
}

export const TipoTarefaService = {
    async getAll(): Promise<TipoTarefa[]> {
        const res = await apiFetch(`${apiUrl}/api/TipoTarefa/BuscarTodos`);
        const result: ApiResponse<TipoTarefa[]> = await res.json();
        return result.sucesso ? result.dados : [];
    },

    async criar(nome: string): Promise<ApiResponse<TipoTarefa>> {
        const res = await apiFetch(`${apiUrl}/api/TipoTarefa/Criar`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ nome }),
        });
        return res.json();
    },

    async atualizar(tipoTarefaId: number, nome: string): Promise<ApiResponse<null>> {
        const res = await apiFetch(`${apiUrl}/api/TipoTarefa/Atualizar`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tipoTarefaId, nome }),
        });
        return res.json();
    },

    async remover(tipoTarefaId: number): Promise<ApiResponse<null>> {
        const res = await apiFetch(`${apiUrl}/api/TipoTarefa/Remover/${tipoTarefaId}`, {
            method: 'DELETE',
        });
        return res.json();
    },
};

export const TarefaService = {
    /**
     * `minhas` = só as do usuário logado (o sino); `abertas` = só as em aberto.
     * `responsavelId` é a visão do gestor — a API só obedece se quem pede for administrador.
     * `de`/`ate` são o intervalo de prazo em ISO: quem calcula é o navegador, que sabe o fuso
     * de quem está olhando (a API não tem como saber que dia é "hoje" para ele).
     */
    async getAll(filtro: {
        minhas?: boolean; abertas?: boolean; responsavelId?: number | null;
        estado?: 'open' | 'done' | 'superseded'; de?: string; ate?: string;
    } = {}): Promise<Tarefa[]> {
        const q = new URLSearchParams();
        if (filtro.minhas) q.set('minhas', 'true');
        if (filtro.abertas) q.set('abertas', 'true');
        if (filtro.responsavelId) q.set('responsavelId', String(filtro.responsavelId));
        if (filtro.estado) q.set('estado', filtro.estado);
        if (filtro.de) q.set('de', filtro.de);
        if (filtro.ate) q.set('ate', filtro.ate);
        const res = await apiFetch(`${apiUrl}/api/Tarefa/BuscarTodos${q.size ? `?${q}` : ''}`);
        const result: ApiResponse<Tarefa[]> = await res.json();
        return result.sucesso && Array.isArray(result.dados) ? result.dados : [];
    },

    /** Registra o desfecho de uma tarefa padrão; a API executa a ação (mover, perder, reagendar, encerrar). */
    async registrarDesfecho(tarefaId: number, dto: RegistrarDesfechoDTO): Promise<ApiResponse<DesfechoRegistrado>> {
        const res = await apiFetch(`${apiUrl}/api/Tarefa/${tarefaId}/Desfecho`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dto),
        });
        return res.json();
    },

    async getByConversa(conversaId: number): Promise<Tarefa[]> {
        const res = await apiFetch(`${apiUrl}/api/Tarefa/BuscarPorConversa/${conversaId}`);
        const result: ApiResponse<Tarefa[]> = await res.json();
        return result.sucesso ? result.dados : [];
    },

    async getById(tarefaId: number): Promise<ApiResponse<Tarefa>> {
        const res = await apiFetch(`${apiUrl}/api/Tarefa/BuscarPorId/${tarefaId}`);
        return res.json();
    },

    async criar(dto: CreateTarefaDTO): Promise<ApiResponse<Tarefa>> {
        const res = await apiFetch(`${apiUrl}/api/Tarefa/Criar`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dto),
        });
        return res.json();
    },

    async atualizar(dto: UpdateTarefaDTO): Promise<ApiResponse<null>> {
        const res = await apiFetch(`${apiUrl}/api/Tarefa/Atualizar`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dto),
        });
        return res.json();
    },

    async remover(tarefaId: number): Promise<ApiResponse<null>> {
        const res = await apiFetch(`${apiUrl}/api/Tarefa/Remover/${tarefaId}`, {
            method: 'DELETE',
        });
        return res.json();
    },
};
